<template>
  <div class="create-page app-page">
    <header class="app-page-header create-header">
      <h1 class="create-title">创作</h1>
    </header>

    <div class="create-body">
      <!-- 类型切换选项卡（始终显示） -->
      <div class="type-tabs">
        <button
          type="button"
          class="type-tab"
          :class="{ active: selectedType === 'quick_video' }"
          @click="selectedType = 'quick_video'"
        >
          <span class="type-tab-icon" v-html="videoIcon" />
          <span class="type-tab-label">视频创作</span>
        </button>
        <button
          type="button"
          class="type-tab"
          :class="{ active: selectedType === 'quick_image' }"
          @click="selectedType = 'quick_image'"
        >
          <span class="type-tab-icon" v-html="imageIcon" />
          <span class="type-tab-label">图片创作</span>
        </button>
      </div>

      <!-- 视频创作表单 -->
      <div v-if="selectedType === 'quick_video'" class="create-form-wrap">
        <div class="ref-strip">
          <button type="button" class="ref-add" @click="uploadLocal">+ 参考</button>
          <button type="button" class="ref-add" @click="openAssetModal">资产库</button>
          <div v-for="a in taskAssetLibrary" :key="a.type + '-' + a.id" class="ref-wrap">
            <button type="button" class="ref-x" @click.stop="removeStripAsset(a)">×</button>
            <div class="ref-thumb" :style="{ backgroundImage: 'url(' + a.cover + ')' }" />
          </div>
          <input ref="localUploadInput" type="file" accept="image/*" hidden @change="onLocalUploadChange" />
        </div>
        <div class="prompt-wrap">
          <div
            ref="promptEditor"
            class="prompt-editor"
            contenteditable="true"
            role="textbox"
            data-placeholder="输入镜头描述；@ 引用参考素材"
            @input="onPromptInput"
            @keydown="onPromptKeydown"
            @blur="onPromptBlur"
          ></div>
          <div v-if="mentionVisible" class="mention-panel">
            <button
              v-for="item in mentionOptions"
              :key="item.type + '-' + item.id"
              type="button"
              class="mention-item"
              @mousedown.prevent="insertMention(item)"
            >
              <span class="mention-item-main">
                <img
                  v-if="item.cover && !isPlaceholderAsset(item.cover)"
                  class="mention-thumb"
                  :src="mediaUrl(item.cover)"
                  :alt="item.name"
                />
                <span v-else class="mention-thumb mention-thumb--empty placeholder-block" />
                <span class="mention-name">@{{ item.name }}</span>
              </span>
              <span class="mention-type">{{ mentionTypeLabel(item.type) }}</span>
            </button>
            <div v-if="!mentionOptions.length" class="mention-empty">暂无可引用素材</div>
          </div>
        </div>
        <div class="toolbar">
          <select v-model="selectedModel" class="tb-sel">
            <option v-for="o in modelOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <select v-model="selectedDuration" class="tb-sel">
            <option v-for="d in durationOptions" :key="d" :value="d">{{ d }}</option>
          </select>
          <select v-model="selectedResolution" class="tb-sel">
            <option v-for="r in resolutionOptions" :key="r" :value="r">{{ r }}</option>
          </select>
          <select v-model="selectedRatio" class="tb-sel">
            <option v-for="r in ratioOptions" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
        </div>
        <button
          type="button"
          class="tb-send"
          :disabled="submitting || !videoPointsPerSecond"
          @click="submitVideo"
        >
          {{ submitting ? '创建中…' : '开始创作 · ' + generatePointsDisplay + ' 积分' }}
        </button>
      </div>

      <!-- 图片创作表单 -->
      <div v-else-if="selectedType === 'quick_image'" class="create-form-wrap">
        <div class="ref-strip">
          <button type="button" class="ref-add" @click="uploadImageLocal">+ 参考</button>
          <button type="button" class="ref-add" @click="openImageAssetModal">资产库</button>
          <div v-for="a in imageAssetLibrary" :key="'img-' + a.id" class="ref-wrap">
            <button type="button" class="ref-x" @click.stop="removeImageAsset(a)">×</button>
            <div class="ref-thumb" :style="{ backgroundImage: 'url(' + a.cover + ')' }" />
          </div>
          <input ref="imageLocalUploadInput" type="file" accept="image/*" hidden @change="onImageLocalUploadChange" />
        </div>
        <div class="prompt-wrap">
          <div
            ref="imagePromptEditor"
            class="prompt-editor"
            contenteditable="true"
            role="textbox"
            :data-placeholder="imageAssetLibrary.length ? '基于参考图描述希望生成的效果…' : '输入图片描述'"
            @input="onImagePromptInput"
          ></div>
        </div>
        <div class="toolbar">
          <select v-model="selectedImageModel" class="tb-sel">
            <option v-for="o in imageModelOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <select v-model="selectedAspectRatio" class="tb-sel">
            <option v-for="r in aspectRatioOptions" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
          <select v-model="selectedImageResolution" class="tb-sel">
            <option v-for="r in imageResolutionOptions" :key="r" :value="r">{{ r }}</option>
          </select>
        </div>
        <button
          type="button"
          class="tb-send"
          :disabled="submitting"
          @click="submitImage"
        >
          {{ submitting ? '创建中…' : '开始创作 · ' + imagePointsDisplay + ' 积分' }}
        </button>
      </div>
    </div>

    <!-- 资产库弹窗（视频 / 图片表单共用，通过 assetModalTarget 区分目标库） -->
    <div v-if="assetModalVisible" class="mask" @click.self="closeAssetModal">
      <div class="modal">
        <h3>资产库</h3>
        <div v-if="visibleAssetTabs.length > 1" class="tabs">
          <button
            v-for="t in visibleAssetTabs"
            :key="t.value"
            type="button"
            :class="{ on: activeAssetType === t.value }"
            @click="switchAssetModalTab(t.value)"
          >{{ t.label }}</button>
        </div>
        <div class="grid">
          <div
            v-for="item in filteredModalAssets"
            :key="item.id + '-' + (item.cover || '')"
            class="grid-item"
            :class="{ on: isTempAssetSelected(item.id) }"
            @click="toggleTempAsset(item.id)"
          >
            <img :src="item.cover" :alt="item.name" />
            <span>{{ item.name }}</span>
          </div>
        </div>
        <div class="modal-ft">
          <button type="button" @click="closeAssetModal">取消</button>
          <button type="button" class="primary" @click="confirmAssets">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "../api/user";

export default {
  name: "CreatePage",
  data() {
    return {
      selectedType: "quick_video",
      submitting: false,

      // 视频表单
      selectedModel: "",
      selectedDuration: "15s",
      selectedResolution: "720p",
      selectedRatio: "16:9",
      ratioOptions: [
        { label: "16:9 横屏", value: "16:9" },
        { label: "9:16 竖屏", value: "9:16" }
      ],
      videoModelsRaw: [],
      taskAssetLibrary: [],
      assetLibrary: [],
      assetModalVisible: false,
      assetModalTarget: "video", // 'video' | 'image'，决定 confirmAssets 写入哪个库
      activeAssetType: "role",
      assetTypes: [
        { value: "role", label: "角色" },
        { value: "scene", label: "场景" },
        { value: "material", label: "素材" }
      ],
      tempSelectedAssetIds: [],
      modalAssetSelectionDraft: { role: [], scene: [], material: [] },
      mentionVisible: false,
      mentionQuery: "",

      // 图片表单
      selectedImageModel: "",
      selectedAspectRatio: "1:1",
      selectedImageResolution: "1K",
      imageAssetLibrary: [], // 图片创作专用参考图列表
      aspectRatioOptions: [
        { label: "1:1 正方形", value: "1:1" },
        { label: "16:9 横屏", value: "16:9" },
        { label: "9:16 竖屏", value: "9:16" },
        { label: "4:3 标准", value: "4:3" }
      ],
      imageResolutionOptions: ["1K", "2K"],
      imageModelsRaw: [],
      imagePointsRaw: 0
    };
  },
  computed: {
    videoIcon() {
      return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
        <path d="M10 10.5v5l5.5-2.5L10 10.5z" fill="currentColor"/>
      </svg>`;
    },
    imageIcon() {
      return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
        <circle cx="8.5" cy="10" r="1.5" fill="currentColor"/>
        <path d="M21 17l-6-6-4 4-3-3-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    },
    modelOptions() {
      return (this.videoModelsRaw || []).map(m => ({
        value: String(m.code || ""),
        label: String(m.name || m.code || "")
      }));
    },
    currentVideoModel() {
      return (this.videoModelsRaw || []).find(m => m.code === this.selectedModel) || null;
    },
    durationOptions() {
      const m = this.currentVideoModel;
      return m && m.durations && m.durations.length ? m.durations : ["4s","5s","6s","7s","8s","9s","10s","11s","12s","13s","14s","15s"];
    },
    resolutionOptions() {
      const m = this.currentVideoModel;
      return m && m.resolutions && m.resolutions.length ? m.resolutions : ["480p","720p","1080p"];
    },
    videoPointsPerSecond() {
      const m = this.currentVideoModel;
      const res = this.selectedResolution;
      const p = m && m.pricing && m.pricing[res];
      return p && p.pointsPerSecond > 0 ? p.pointsPerSecond : 0;
    },
    generatePointsDisplay() {
      const sec = this.parseDurationSeconds(this.selectedDuration);
      const rate = this.videoPointsPerSecond;
      if (!rate || !sec) return "—";
      return String(Math.max(1, Math.round(rate * sec)));
    },
    mentionSourceLibrary() {
      const map = new Map();
      const add = x => {
        if (!x || !x.name) return;
        const t = this.normType(x.type);
        map.set(`${t}:${x.name}`, { ...x, type: t });
      };
      this.taskAssetLibrary.forEach(add);
      return [...map.values()];
    },
    mentionPickerLibrary() {
      return this.mentionSourceLibrary;
    },
    mentionOptions() {
      const kw = this.mentionQuery.toLowerCase();
      const source = this.mentionPickerLibrary;
      if (!kw) return source.slice(0, 8);
      const matched = source.filter(x => String(x.name).toLowerCase().includes(kw));
      return matched.length ? matched.slice(0, 8) : source.slice(0, 8);
    },
    mentionAssetByNameFirst() {
      const m = new Map();
      this.taskAssetLibrary.forEach(it => {
        const n = String(it.name || "").trim();
        if (!n || m.has(n)) return;
        m.set(n, { ...it, type: this.normType(it.type) });
      });
      return m;
    },
    filteredModalAssets() {
      return this.assetLibrary.filter(x => x.type === this.activeAssetType);
    },
    // 资产库弹窗要展示的 tab 列表：图片创作只显示「素材」一类；
    // 视频创作按 assetTypes 全部显示。
    visibleAssetTabs() {
      if (this.assetModalTarget === "image") {
        return this.assetTypes.filter(t => t.value === "material");
      }
      return this.assetTypes;
    },
    imageModelOptions() {
      return (this.imageModelsRaw || []).map(m => ({
        value: String(m.code || ""),
        label: String(m.name || m.code || "")
      }));
    },
    currentImageModel() {
      return (this.imageModelsRaw || []).find(m => m.code === this.selectedImageModel) || null;
    },
    imagePointsDisplay() {
      const m = this.currentImageModel;
      if (m && Number(m.pointsPerCall) > 0) return String(Number(m.pointsPerCall));
      return String(this.imagePointsRaw || 0);
    }
  },
  watch: {
    selectedModel() {
      this.$nextTick(() => this.syncDurRes());
    }
  },
  async mounted() {
    await Promise.all([this.loadVideoModels(), this.loadImageModels(), this.loadAssets()]);
  },
  methods: {
    mediaUrl(s) {
      s = String(s || "").trim();
      if (!s) return "";
      return /^https?:\/\//i.test(s) ? s : (s.startsWith("/") ? s : `/${s}`);
    },
    isPlaceholderAsset(src) {
      return /dummyimage\.com/i.test(String(src || ""));
    },
    parseDurationSeconds(v) {
      const m = String(v).match(/^(\d+)\s*s$/i);
      return m ? Math.max(4, parseInt(m[1], 10)) : 15;
    },
    normType(t) {
      t = String(t || "").toLowerCase();
      return t === "role" || t === "scene" ? t : "material";
    },
    mentionTypeLabel(t) {
      return t === "role" ? "角色" : t === "scene" ? "场景" : "素材";
    },
    async loadVideoModels() {
      try {
        const list = await userApi.videoModels();
        this.videoModelsRaw = Array.isArray(list) ? list : [];
      } catch (e) {
        this.videoModelsRaw = [];
      }
      if (this.videoModelsRaw.length && !this.selectedModel) {
        this.selectedModel = this.videoModelsRaw[0].code;
      }
      this.syncDurRes();
    },
    async loadImageModels() {
      try {
        const list = await userApi.imageModels();
        this.imageModelsRaw = Array.isArray(list) ? list : [];
        if (this.imageModelsRaw.length && !this.selectedImageModel) {
          this.selectedImageModel = this.imageModelsRaw[0].code;
        }
        // 积分：直接取 imageModels 返回的 pointsPerCall，与 QuickImagePage 保持一致。
        if (this.imageModelsRaw.length) {
          const first = this.imageModelsRaw[0];
          const ppc = first ? Number(first.pointsPerCall) || 0 : 0;
          this.imagePointsRaw = ppc > 0 ? ppc : 0;
        }
      } catch (e) {
        this.imageModelsRaw = [];
      }
    },
    syncDurRes() {
      if (this.durationOptions.indexOf(this.selectedDuration) < 0) {
        this.selectedDuration = this.durationOptions[this.durationOptions.length - 1];
      }
      if (this.resolutionOptions.indexOf(this.selectedResolution) < 0) {
        this.selectedResolution = this.resolutionOptions[0];
      }
      if (!this.ratioOptions.some(r => r.value === this.selectedRatio)) {
        this.selectedRatio = this.ratioOptions[0].value;
      }
    },
    async loadAssets() {
      try {
        const data = await userApi.assets();
        this.assetLibrary = ((data && data.image) || []).map(it => ({
          id: it.id,
          type: it.assetType || "material",
          name: it.name || "",
          cover: this.mediaUrl(it.cover || it.url)
        }));
      } catch (e) {
        this.assetLibrary = [];
      }
    },
    getPromptText() {
      const ed = this.$refs.promptEditor;
      if (!ed) return "";
      const raw = ed.querySelector(".mention-tag")
        ? this.serializePromptEditor(ed)
        : String(ed.innerText || "");
      return this.normalizePromptLineBreaks(raw.replace(/\u00A0/g, " ")).trim();
    },
    normalizePromptLineBreaks(text) {
      return String(text || "")
        .replace(/(@[^\s@]+)[ \t]*\r?\n[ \t]*/g, "$1 ")
        .replace(/\r?\n[ \t]*(?=@)/g, " ");
    },
    serializePromptEditor(editor) {
      const parts = [];
      const walk = node => {
        if (!node) return;
        if (node.nodeType === Node.TEXT_NODE) { parts.push(node.nodeValue || ""); return; }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.classList && node.classList.contains("mention-tag")) {
          const label = String(node.getAttribute("data-label") || "").trim();
          if (label) { parts.push(`@${label}`); return; }
          const textEl = node.querySelector(".mention-tag-text");
          const raw = textEl ? String(textEl.textContent || "") : "";
          parts.push(raw.startsWith("@") ? raw : `@${raw.replace(/^@/, "")}`);
          return;
        }
        Array.from(node.childNodes || []).forEach(walk);
      };
      walk(editor);
      return parts.join("");
    },
    escapeHtml(s) {
      return String(s || "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    },
    buildRefs(text) {
      const names = [];
      const seen = new Set();
      String(text).replace(/@([^\s@]+)/g, (_, n) => {
        n = n.trim();
        if (n && !/^Image\d+$/i.test(n) && !seen.has(n)) { seen.add(n); names.push(n); }
        return "";
      });
      const byName = new Map(this.mentionSourceLibrary.map(x => [x.name, x]));
      const w = { role: 0.9, scene: 0.7, material: 0.6 };
      const out = [];
      const sk = new Set();
      const push = (hit, name) => {
        const key = `${hit.type}:${name}`;
        if (sk.has(key) || !hit.cover) return;
        sk.add(key);
        out.push({ type: "image", url: hit.cover, weight: w[hit.type] || 0.7, bindType: hit.type, bindName: name });
      };
      names.forEach(n => {
        const hit = byName.get(n);
        if (hit) push(hit, n);
      });
      this.taskAssetLibrary.forEach(it => push(it, it.name));
      return out.slice(0, 9);
    },
    toBackendPrompt(display, refs) {
      const map = new Map();
      refs.forEach((r, i) => { if (r.bindName) map.set(r.bindName, i + 1); });
      return String(display)
        .replace(/@([^\s@]+)/g, (full, name) => {
          const i = map.get(String(name).trim());
          return i ? `@Image${i}` : full;
        })
        .replace(/^\s*生成语言\s*[：:][^\n]*\n?/gm, "")
        .trim();
    },
    onPromptInput() {
      const ed = this.$refs.promptEditor;
      const sel = window.getSelection();
      if (!ed) { this.mentionVisible = false; return; }
      let textBefore = this.getPromptText();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0).cloneRange();
        range.selectNodeContents(ed);
        range.setEnd(sel.anchorNode, sel.anchorOffset);
        textBefore = range.toString();
      }
      const m = String(textBefore || "").replace(/\uFF20/g, "@").match(/@\s*([^\s@]*)$/);
      if (!m) { this.mentionVisible = false; this.mentionQuery = ""; return; }
      this.mentionQuery = String(m[1] || "").trim();
      this.mentionVisible = true;
    },
    onPromptKeydown(e) {
      if (e.key === "@") {
        this.$nextTick(() => { this.mentionQuery = ""; this.mentionVisible = true; });
      }
      if (!this.mentionVisible) return;
      if (e.key === "Enter" && this.mentionOptions.length) { e.preventDefault(); this.insertMention(this.mentionOptions[0]); }
      if (e.key === "Escape") this.mentionVisible = false;
    },
    onPromptBlur() {
      setTimeout(() => { this.mentionVisible = false; this.autoTagMentionsInEditor(); }, 120);
    },
    removeMentionTriggerBeforeCaret(selection) {
      const anchor = selection.anchorNode;
      const offset = selection.anchorOffset;
      if (!anchor || anchor.nodeType !== Node.TEXT_NODE) return;
      const text = anchor.textContent || "";
      const prefix = text.slice(0, offset);
      const suffix = text.slice(offset);
      const match = prefix.replace(/\uFF20/g, "@").match(/@\s*([^\s@]*)$/);
      if (!match) return;
      const start = prefix.length - match[0].length;
      anchor.textContent = `${prefix.slice(0, start)}${suffix}`;
      const range = document.createRange();
      range.setStart(anchor, start);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    createMentionNode(item) {
      const mention = document.createElement("span");
      mention.className = "mention-tag";
      mention.setAttribute("contenteditable", "false");
      mention.setAttribute("data-id", String(item.id || ""));
      mention.setAttribute("data-type", item.type || "material");
      mention.setAttribute("data-label", item.name || "");
      if (item.cover) {
        const img = document.createElement("img");
        img.className = "mention-tag-img";
        img.src = item.cover;
        img.alt = item.name || "";
        img.setAttribute("draggable", "false");
        mention.appendChild(img);
      }
      const text = document.createElement("span");
      text.className = "mention-tag-text";
      text.textContent = `@${item.name}`;
      mention.appendChild(text);
      return mention;
    },
    insertMention(item) {
      const ed = this.$refs.promptEditor;
      const sel = window.getSelection();
      if (!ed || !sel || !sel.rangeCount) return;
      this.removeMentionTriggerBeforeCaret(sel);
      const range = sel.getRangeAt(0);
      const mention = this.createMentionNode(item);
      const trailingSpace = document.createTextNode(" ");
      range.deleteContents();
      range.insertNode(trailingSpace);
      range.insertNode(mention);
      range.setStartAfter(trailingSpace);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      this.mentionVisible = false;
      this.mentionQuery = "";
      ed.focus();
    },
    autoTagMentionsInEditor() {
      const editor = this.$refs.promptEditor;
      if (!editor) return;
      const byName = this.mentionAssetByNameFirst;
      if (!byName || !byName.size) return;
      const sortedNames = [...byName.keys()].sort((a, b) => b.length - a.length);
      const isInsideMention = node => {
        let p = node && node.parentNode;
        while (p && p !== editor) {
          if (p.nodeType === Node.ELEMENT_NODE && p.classList && p.classList.contains("mention-tag")) return true;
          p = p.parentNode;
        }
        return false;
      };
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
          if (!node || !node.nodeValue || isInsideMention(node)) return NodeFilter.FILTER_REJECT;
          const nv = String(node.nodeValue || "");
          if (nv.indexOf("@") === -1 && nv.indexOf("\uFF20") === -1) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach(node => {
        const text = String(node.nodeValue || "");
        const frag = document.createDocumentFragment();
        let changed = false;
        let i = 0;
        while (i < text.length) {
          const c = text[i];
          if (c !== "@" && c !== "\uFF20") {
            let nextAt = text.length;
            for (let j = i + 1; j < text.length; j++) {
              if (text[j] === "@" || text[j] === "\uFF20") { nextAt = j; break; }
            }
            frag.appendChild(document.createTextNode(text.slice(i, nextAt)));
            i = nextAt;
            continue;
          }
          const rest = text.slice(i + 1);
          let hitName = null;
          for (let si = 0; si < sortedNames.length; si++) {
            if (rest.startsWith(sortedNames[si])) { hitName = sortedNames[si]; break; }
          }
          if (hitName && byName.has(hitName)) {
            frag.appendChild(this.createMentionNode(byName.get(hitName)));
            frag.appendChild(document.createTextNode(" "));
            i += 1 + hitName.length;
            changed = true;
          } else {
            frag.appendChild(document.createTextNode(c));
            i += 1;
          }
        }
        if (changed) node.parentNode.replaceChild(frag, node);
      });
    },
    openAssetModal() {
      this.assetModalTarget = "video";
      const draft = { role: [], scene: [], material: [] };
      this.taskAssetLibrary.forEach(it => {
        const t = this.normType(it.type);
        if (!draft[t].includes(it.id)) draft[t].push(it.id);
      });
      this.modalAssetSelectionDraft = draft;
      // 进入弹窗时 activeAssetType 切回 role（视频表单默认）
      this.activeAssetType = "role";
      this.tempSelectedAssetIds = [...draft[this.activeAssetType]];
      this.assetModalVisible = true;
    },
    openImageAssetModal() {
      this.assetModalTarget = "image";
      // 图片表单只取素材类；激活 tab 切到 material
      this.activeAssetType = "material";
      const draft = { role: [], scene: [], material: [] };
      this.imageAssetLibrary.forEach(it => {
        const t = this.normType(it.type) || "material";
        if (!draft[t].includes(it.id)) draft[t].push(it.id);
      });
      this.modalAssetSelectionDraft = draft;
      this.tempSelectedAssetIds = [...(draft.material || [])];
      this.assetModalVisible = true;
    },
    switchAssetModalTab(t) {
      this.modalAssetSelectionDraft[this.activeAssetType] = [...this.tempSelectedAssetIds];
      this.activeAssetType = t;
      this.tempSelectedAssetIds = [...(this.modalAssetSelectionDraft[t] || [])];
    },
    closeAssetModal() { this.assetModalVisible = false; },
    isTempAssetSelected(id) { return this.tempSelectedAssetIds.some(x => String(x) === String(id)); },
    toggleTempAsset(id) {
      const s = String(id);
      const list = this.tempSelectedAssetIds;
      const i = list.findIndex(x => String(x) === s);
      this.tempSelectedAssetIds = i < 0 ? [...list, id] : list.filter(x => String(x) !== s);
    },
    confirmAssets() {
      const cur = this.activeAssetType;
      const draft = { ...this.modalAssetSelectionDraft, [cur]: [...this.tempSelectedAssetIds] };
      const byKey = new Map(this.assetLibrary.map(x => [`${x.type}:${x.id}`, x]));
      if (this.assetModalTarget === "image") {
        // 图片表单：只取 material 类作为参考图，写入 imageAssetLibrary
        const next = [];
        (draft.material || []).forEach(id => {
          const it = byKey.get(`material:${id}`);
          if (it) next.push({ ...it, type: "material" });
        });
        this.imageAssetLibrary = next;
        this.assetModalVisible = false;
        return;
      }
      // 视频表单：把三类全部写入 taskAssetLibrary
      const next = [];
      ["role", "scene", "material"].forEach(t => {
        (draft[t] || []).forEach(id => {
          const it = byKey.get(`${t}:${id}`);
          if (it) next.push({ ...it, type: t });
        });
      });
      this.taskAssetLibrary = next;
      this.assetModalVisible = false;
    },
    removeStripAsset(a) { this.taskAssetLibrary = this.taskAssetLibrary.filter(x => !(x.type === a.type && x.id === a.id)); },
    removeImageAsset(a) { this.imageAssetLibrary = this.imageAssetLibrary.filter(x => String(x.id) !== String(a.id)); },
    uploadLocal() {
      const inp = this.$refs.localUploadInput;
      if (inp) inp.click();
    },
    uploadImageLocal() {
      const inp = this.$refs.imageLocalUploadInput;
      if (inp) inp.click();
    },
    async onLocalUploadChange(ev) {
      const file = ev.target && ev.target.files && ev.target.files[0];
      if (!file) return;
      try {
        const r = await userApi.uploadAsset({ file, assetType: this.activeAssetType, name: file.name });
        const item = { id: r.id || `local-${Date.now()}`, type: this.activeAssetType, name: r.name || file.name, cover: this.mediaUrl(r.cover || r.url) };
        if (!this.taskAssetLibrary.some(x => x.type === item.type && x.name === item.name)) this.taskAssetLibrary.push(item);
        if (!this.assetLibrary.some(x => String(x.id) === String(item.id))) this.assetLibrary.push(item);
      } catch (e) {
        window.alert((e && e.message) || "上传失败");
      }
      ev.target.value = "";
    },
    async onImageLocalUploadChange(ev) {
      const file = ev.target && ev.target.files && ev.target.files[0];
      if (!file) return;
      try {
        // 图片创作的参考图统一归类为 material
        const r = await userApi.uploadAsset({ file, assetType: "material", name: file.name });
        const item = { id: r.id || `local-${Date.now()}`, type: "material", name: r.name || file.name, cover: this.mediaUrl(r.cover || r.url) };
        if (!this.imageAssetLibrary.some(x => String(x.id) === String(item.id) || x.name === item.name)) this.imageAssetLibrary.push(item);
        if (!this.assetLibrary.some(x => String(x.id) === String(item.id))) this.assetLibrary.push(item);
      } catch (e) {
        window.alert((e && e.message) || "上传失败");
      }
      ev.target.value = "";
    },

    // --- 图片表单 ---
    getImagePromptText() {
      const ed = this.$refs.imagePromptEditor;
      return ed ? String(ed.innerText || "").trim() : "";
    },
    onImagePromptInput() {
      // 纯文字输入，无需特殊处理
    },

    // --- 提交 ---
    async submitVideo() {
      if (this.submitting) return;
      const display = this.getPromptText();
      if (!display) { window.alert("请先输入镜头描述"); return; }
      if (!this.videoPointsPerSecond) { window.alert("当前模型未配置扣费"); return; }
      const refs = this.buildRefs(display);
      const prompt = this.toBackendPrompt(display, refs);
      const durationSeconds = this.parseDurationSeconds(this.selectedDuration);
      const title = display.slice(0, 20) + (display.length > 20 ? "…" : "");
      this.submitting = true;
      try {
        const conv = await userApi.createConversation({ type: "quick_video", title });
        const convId = Number(conv && conv.id) || 0;
        if (!convId) throw new Error("创建对话失败");
        await userApi.createQuickVideoTask({
          modelCode: this.selectedModel,
          resolution: this.selectedResolution,
          durationSeconds,
          prompt,
          references: JSON.stringify(refs),
          aspectRatio: this.selectedRatio,
          conversationId: convId
        });
        // 触发左侧历史对话列表刷新（新对话立即可见）
        try { this.$root && this.$root.$emit && this.$root.$emit("conversations-changed"); } catch (e) {}
        this.$router.push("/quick-video?id=" + convId).catch(() => {});
      } catch (e) {
        window.alert((e && e.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    },

    async submitImage() {
      if (this.submitting) return;
      const prompt = this.getImagePromptText();
      if (!prompt) { window.alert("请先输入图片描述"); return; }
      const title = prompt.slice(0, 20) + (prompt.length > 20 ? "…" : "");
      // 收集参考图 URL 列表（去重 + 过滤空）+ 主参考图（取首张，与「sourceImageUrl」语义对齐）
      const seenRef = new Set();
      const refUrls = [];
      let sourceImageUrl = "";
      (this.imageAssetLibrary || []).forEach(a => {
        const u = this.mediaUrl(a && a.cover);
        if (!u || seenRef.has(u)) return;
        seenRef.add(u);
        if (!sourceImageUrl) sourceImageUrl = u;
        refUrls.push(u);
      });
      this.submitting = true;
      try {
        const conv = await userApi.createConversation({ type: "quick_image", title });
        const convId = Number(conv && conv.id) || 0;
        if (!convId) throw new Error("创建对话失败");
        await userApi.createQuickImageTask({
          modelCode: this.selectedImageModel,
          prompt,
          aspectRatio: this.selectedAspectRatio,
          resolution: this.selectedImageResolution,
          sourceImageUrl,
          references: JSON.stringify(refUrls),
          conversationId: convId
        });
        // 触发左侧历史对话列表刷新（新对话立即可见）
        try { this.$root && this.$root.$emit && this.$root.$emit("conversations-changed"); } catch (e) {}
        this.$router.push("/quick-image?id=" + convId).catch(() => {});
      } catch (e) {
        window.alert((e && e.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.create-page {
  display: flex;
  flex-direction: column;
  background: #333333;
}
.create-header {
  padding: 14px 18px 6px;
  flex-shrink: 0;
}
.create-title {
  margin: 0;
  font-size: 20px;
  color: #e9e9e9;
}
.create-body {
  flex: 1;
  padding: 24px 16px;
  overflow-y: auto;
}
/* 创作类型选项卡（始终显示） */
.type-tabs {
  display: flex;
  gap: 8px;
  max-width: 1000px;
  margin: 0 auto 16px;
  background: #1f1f1f;
  border-radius: 12px;
  padding: 4px;
}
.type-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
}
.type-tab:hover:not(.active) {
  color: #e9e9e9;
  background: #2a2a2a;
}
.type-tab.active {
  background: #19b06b;
  color: #ffffff;
}
.type-tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
.type-tab-icon :deep(svg) {
  width: 18px;
  height: 18px;
}
.type-tab-label {
  font-size: 14px;
  font-weight: 500;
}

.create-form-wrap {
  max-width: 1000px;
  margin: 0 auto;
}
.prompt-wrap {
  position: relative;
  margin-bottom: 10px;
}
.prompt-editor {
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  min-height: 200px;
  padding: 12px;
  font-size: 14px;
  color: #e9e9e9;
  line-height: 1.6;
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
}
.prompt-editor:empty::before {
  content: attr(data-placeholder);
  color: #6b7280;
  pointer-events: none;
}
.prompt-editor:focus {
  border-color: #19b06b;
}
.mention-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  margin-top: 4px;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
}
.mention-item {
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #e9e9e9;
  font-size: 13px;
}
.mention-item:hover {
  background: #3a3a3a;
}
.mention-item-main {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mention-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}
.mention-thumb--empty {
  background: #444;
}
.mention-name {
  font-size: 13px;
}
.mention-type {
  font-size: 14px;
  color: #6b7280;
}
.mention-empty {
  padding: 12px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.tb-sel {
  height: 34px;
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: #e9e9e9;
  font-size: 13px;
  padding: 0 8px;
  cursor: pointer;
}
.tb-send {
  width: 100%;
  height: 44px;
  background: #19b06b;
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.tb-send:hover:not(:disabled) {
  background: #17a062;
}
.tb-send:disabled {
  background: #2a5a3a;
  color: #6b7280;
  cursor: not-allowed;
}
.ref-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.ref-add,
.ref-wrap {
  position: relative;
}
.ref-add {
  width: 52px;
  height: 52px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #2a2a2a;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.ref-thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-color: #2a2a2a;
}
.ref-x {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  background: #773131;
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.mask {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 16px;
  width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 {
  margin: 0;
  color: #e9e9e9;
  font-size: 16px;
}
.tabs {
  display: flex;
  gap: 6px;
}
.tabs button {
  padding: 4px 12px;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  background: #333;
  color: #9ca3af;
  cursor: pointer;
  font-size: 14px;
}
.tabs button.on {
  background: #19b06b;
  color: #fff;
  border-color: #19b06b;
}
.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  overflow-y: auto;
  max-height: 300px;
}
.grid-item {
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #333;
  text-align: center;
}
.grid-item img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
}
.grid-item span {
  display: block;
  font-size: 14px;
  color: #9ca3af;
  padding: 4px 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.grid-item.on {
  border-color: #19b06b;
}
.modal-ft {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.modal-ft button {
  height: 34px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  background: #3a3a3a;
  color: #e9e9e9;
}
.modal-ft button.primary {
  background: #19b06b;
  color: #fff;
}
</style>
